import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PrimaryButton, SecondaryButton } from '../../../../Button';
import {
  IMeasureDisplayFormatFieldsFragment,
  MeasureDisplayFormatKind,
} from '../../../../../utils/apollo/graphqlSchema';
import {
  MeasureDisplayFormatCalculation,
  MeasureDisplayFormatCalculationUnit,
  MeasureDisplayFormatCalculationValue,
  MeasureDisplayFormatKindField,
  MeasureDisplayFormatPrecision,
  MeasureDisplayFormatUnit,
} from '../../../MeasureDisplayFormat';
import { measureDisplayFormatSchema } from '../../../../../utils/builder/measures/measureDisplayFormat/validationSchemas';
import * as FormatMenuUI from './FormatMenuUI';
import { IMeasureFormatMenuState } from './validation';
import { getDefaultMeasureFormatMenuState } from './utils';
import isNil from 'lodash/isNil';
import { defaultMeasureDisplayFormat } from '../../../../../utils/builder/measures/utils';
import { defaultMeasureDisplayFormatInput } from '../../../../../utils/builder/measures/measureDisplayFormat/utils';
import { useChartContext } from '../../../ChartContext/ChartContext';
import { YAxisPosition } from '../../../interfaces';
import { getYAxisLabelByPosition } from '../../../CentralChartSetting/assignmentComponents/utils';
interface IProps {
  measureKey: string;
  onSubmit: (values: IMeasureDisplayFormatFieldsFragment) => void;
  hideCalculation: boolean;
}

export default ({ onSubmit, measureKey, hideCalculation }: IProps) => {
  const {
    settings: {
      chartSettings: { displayedMeasureSettings, axisSettings },
    },
  } = useChartContext();

  // this is the only place we need to get the measure.format directly from displayedMeasureSettings
  // this can help user know what format is applied to the measure, but not final result after determine
  // whether to use YAxis Format or Measure Format.
  const measureSetting = displayedMeasureSettings.find(
    (measure) => measure.key === measureKey,
  );
  const measureFormat = measureSetting?.format ?? null;
  const defaultValues = getDefaultMeasureFormatMenuState(measureFormat);

  const formMethods = useForm<IMeasureFormatMenuState>({
    resolver: zodResolver(measureDisplayFormatSchema),
    defaultValues,
  });

  const currentYAxisSetting =
    axisSettings.yAxisSettings[
      measureSetting?.yAxisPosition ?? YAxisPosition.LEFT
    ];

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isDirty },
    reset,
    resetField,
  } = formMethods;

  const formatKind = useWatch({
    name: 'kind',
    control,
  });
  const calculationUnit = useWatch({
    name: 'calculationUnit',
    control,
  });

  const handleChangeKind = () => {
    // Change of format kind will reset entire form
    resetField('decimalPlace', {
      defaultValue: defaultMeasureDisplayFormatInput.decimalPlace,
    });
    resetField('calculationUnit', {
      defaultValue: defaultMeasureDisplayFormatInput.calculationUnit,
    });
    resetField('calculationValue', {
      defaultValue: defaultMeasureDisplayFormatInput.calculationValue,
    });
    resetField('unit', { defaultValue: defaultMeasureDisplayFormatInput.unit });
  };

  const handleReset = () => {
    reset(defaultValues);
  };

  const onSubmitForm = handleSubmit((formData) => {
    const result: IMeasureDisplayFormatFieldsFragment = {
      ...defaultMeasureDisplayFormat,
      kind: formData.kind,
      decimalPlace: formData.decimalPlace,
    };

    if (
      formData.kind === MeasureDisplayFormatKind.Number &&
      !isNil(formData.unit)
    ) {
      result.unit = formData.unit;
    }

    if (formData.calculationUnit && formData.calculationValue) {
      result.calculationUnit = formData.calculationUnit;
      result.calculationValue = formData.calculationValue;
    }

    onSubmit(result);
  });

  return (
    <FormProvider {...formMethods}>
      <FormatMenuUI.FormContainer onSubmit={onSubmitForm}>
        {currentYAxisSetting.isApplyYAxisFormat && (
          <FormatMenuUI.StyledHintBlock
            hint={`The ${getYAxisLabelByPosition(
              measureSetting?.yAxisPosition,
            )} Y-Axis has a format applied.  Changes to the measure's format will not be reflected.`}
          />
        )}
        <FormatMenuUI.FieldsContainer>
          <Controller
            name="kind"
            control={control}
            render={({ field }) => (
              <MeasureDisplayFormatKindField
                value={field.value}
                onChange={(newKind) => {
                  field.onChange(newKind);
                  handleChangeKind();
                }}
              />
            )}
          />
          <Controller
            name="decimalPlace"
            control={control}
            render={({ field }) => (
              <MeasureDisplayFormatPrecision
                value={!isNil(field.value) ? field.value.toString() : null}
                onChange={(newValue) => field.onChange(Number(newValue))}
              />
            )}
          />
          {!hideCalculation && (
            <MeasureDisplayFormatCalculation>
              <Controller
                name="calculationUnit"
                control={control}
                render={({ field }) => (
                  <MeasureDisplayFormatCalculationUnit
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {calculationUnit && (
                <Controller
                  name="calculationValue"
                  control={control}
                  render={({ field }) => (
                    <MeasureDisplayFormatCalculationValue
                      value={
                        !isNil(field.value) ? field.value.toString() : null
                      }
                      onChange={(newValue) =>
                        field.onChange(
                          !isNil(newValue) ? Number(newValue) : null,
                        )
                      }
                    />
                  )}
                />
              )}
            </MeasureDisplayFormatCalculation>
          )}
          {formatKind === MeasureDisplayFormatKind.Number && (
            <Controller
              name="unit"
              control={control}
              render={({ field }) => (
                <MeasureDisplayFormatUnit
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </FormatMenuUI.FieldsContainer>
        <FormatMenuUI.ActionsContainer>
          <SecondaryButton
            type="button"
            onClick={handleReset}
            size="regular"
            disabled={isSubmitting || !isDirty}
            trackContext="format"
            trackAction="reset"
          >
            Reset
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            onClick={onSubmitForm}
            size="regular"
            disabled={isSubmitting || !isDirty}
            trackContext="format"
            trackAction="confirm"
          >
            Confirm
          </PrimaryButton>
        </FormatMenuUI.ActionsContainer>
      </FormatMenuUI.FormContainer>
    </FormProvider>
  );
};
